import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ButtonHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { createPortal } from 'react-dom'
import { KwSearchField } from '../atoms/KwSearchField'
import { color, feed, font, layout, radius, shadow } from '../tokens'
import { popoverOpensBelow, type PopoverPlacement } from './popover-placement'
import { AnchoredPopover } from './AnchoredPopover'
import './property-chip.css'
import { headerControlStyle } from './header-control'

export type ChipPopoverOption = {
  value: string
  label: string
  /** Full label in dropdown when trigger uses shorter `label`. */
  menuLabel?: string
  /** Second line in person-style menus (e.g. email). */
  subtitle?: string
  /** Muted „clear“ row (e.g. Nicht zugewiesen). */
  clear?: boolean
  /** Person row — show avatar with initials. */
  initials?: string
  /** Horizontal rule after this option. */
  dividerAfter?: boolean
  /** Sticky section header key (same key = one sticky group). KA-1216 */
  groupKey?: string
  /** Section header label (Firma / Orga). Shown sticky above options with same groupKey. */
  groupLabel?: string
  /** Leading color swatch (project accent). 8×8 square. */
  colorDot?: string
}

const chip = layout.chipPopover

type ListRow =
  | { kind: 'header'; key: string; label: string }
  | { kind: 'option'; opt: ChipPopoverOption }

function buildGroupedRows(options: ChipPopoverOption[]): ListRow[] {
  const rows: ListRow[] = []
  let lastKey: string | undefined
  for (const opt of options) {
    const key = opt.groupKey
    const label = opt.groupLabel?.trim()
    if (key && label && key !== lastKey) {
      rows.push({ kind: 'header', key, label })
      lastKey = key
    } else if (!key) {
      lastKey = undefined
    }
    rows.push({ kind: 'option', opt })
  }
  return rows
}

/**
 * Pill-trigger dropdown — custom popover (design tokens), no native &lt;select&gt; list.
 * Menu is portaled to document.body (fixed) so overflow:hidden ancestors cannot clip it.
 * Flip up when viewport space below &lt; layout.chipPopover.flipMinBelowPx (KA-388).
 * Groups: sticky `groupLabel` headers · optional `colorDot` (KA-1216).
 */
export function ChipPopoverSelect({
  value,
  onChange,
  options,
  pillStyle,
  ariaLabel,
  prefix,
  disabled = false,
  fullWidth = false,
  /** Over sheets (z≈9700) higher — otherwise menu opens behind the dialog. */
  menuZIndex = 9000,
  /** When search yields a custom value (e.g. free-typed time), show as selectable row. */
  allowCustom,
  alwaysShowSearch = false,
  wrapOptionLabels = false,
  searchPlaceholder = 'Suchen…',
  /** Override menu width floor (default layout.chipPopover.minWidthPx). */
  menuMinWidthPx,
  /** Override menu width cap (default layout.chipPopover.maxWidthPx). */
  menuMaxWidthPx,
  placement = 'auto',
  appearance = 'property',
  triggerProps,
}: {
  value: string
  onChange: (value: string) => void
  options: ChipPopoverOption[]
  pillStyle: (value: string, label: string) => React.CSSProperties
  ariaLabel: string
  prefix?: React.ReactNode
  fullWidth?: boolean
  disabled?: boolean
  menuZIndex?: number
  allowCustom?: (query: string) => ChipPopoverOption | null
  /** Keep search available even for short project/contact lists. */
  alwaysShowSearch?: boolean
  /** Preserve complete option names in roomy project/company pickers. Trigger remains compact. */
  wrapOptionLabels?: boolean
  searchPlaceholder?: string
  menuMinWidthPx?: number
  menuMaxWidthPx?: number
  placement?: PopoverPlacement
  appearance?: 'property' | 'header' | 'field'
  triggerProps?: Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'id' | 'autoFocus' | 'tabIndex' | 'aria-label' | 'aria-describedby' | 'aria-invalid' | 'aria-required'>
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
  const rootRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const searchId = useId()
  const minW = menuMinWidthPx ?? chip.minWidthPx
  const maxW = menuMaxWidthPx ?? chip.maxWidthPx
  const current = options.find((o) => o.value === value) ?? (
    value ? { value, label: value } : options[0]
  )
  const showSearch = alwaysShowSearch || options.length >= chip.searchThreshold || Boolean(allowCustom)

  const customOpt = useMemo(() => {
    if (!allowCustom) return null
    const needle = query.trim()
    if (!needle) return null
    const custom = allowCustom(needle)
    if (!custom) return null
    if (options.some((o) => o.value === custom.value)) return null
    return custom
  }, [allowCustom, options, query])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const base = !needle
      ? options
      : options.filter((opt) => {
          if (opt.clear) return true
          const hay = `${opt.menuLabel ?? ''} ${opt.label} ${opt.subtitle ?? ''} ${opt.groupLabel ?? ''}`.toLowerCase()
          return hay.includes(needle)
        })
    if (customOpt) return [customOpt, ...base]
    return base
  }, [options, query, customOpt])

  const selectable = useMemo(() => {
    return filtered.filter((o) => {
      if (!query.trim()) return true
      if (o.clear) return filtered.some((x) => !x.clear)
      return true
    })
  }, [filtered, query])

  const displayRows = useMemo(() => buildGroupedRows(selectable), [selectable])

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return
    const update = () => {
      if (!btnRef.current) return
      const rect = btnRef.current.getBoundingClientRect()
      const menuWidth = Math.min(window.innerWidth - 2 * chip.edgePadPx, appearance === 'field' ? rect.width : Math.min(maxW, Math.max(rect.width, minW)))
      const left = Math.min(
        Math.max(chip.edgePadPx, rect.left),
        window.innerWidth - menuWidth - chip.edgePadPx,
      )
      const spaceBelow = window.innerHeight - rect.bottom - chip.edgePadPx
      const spaceAbove = rect.top - chip.edgePadPx
      const required = placement === 'auto' ? chip.flipMinBelowPx : Math.min(chip.maxHeightPx, menuRef.current?.getBoundingClientRect().height || chip.flipMinBelowPx)
      const preferBelow = popoverOpensBelow(placement, spaceAbove, spaceBelow, required)
      const available = preferBelow ? spaceBelow : spaceAbove
      setMenuStyle({
        position: 'fixed',
        top: preferBelow ? rect.bottom + chip.offsetPx : undefined,
        bottom: preferBelow ? undefined : window.innerHeight - rect.top + chip.offsetPx,
        left,
        width: menuWidth,
        maxHeight: Math.min(chip.maxHeightPx, Math.max(120, available)),
        zIndex: menuZIndex,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, showSearch, filtered.length, menuZIndex, minW, maxW, placement, appearance])

  useEffect(() => {
    if (!open || appearance !== 'field' || showSearch) return
    const active=menuRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]') ?? menuRef.current?.querySelector<HTMLButtonElement>('[role="option"]')
    active?.focus({preventScroll:true})
  }, [open, appearance, showSearch])

  useEffect(() => { if(disabled) setOpen(false) }, [disabled])

  useEffect(() => {
    if (!open) {
      setQuery('')
      return
    }
    if (showSearch) {
      const t = window.setTimeout(() => searchRef.current?.focus(), 0)
      return () => window.clearTimeout(t)
    }
  }, [open, showSearch])

  useLayoutEffect(() => {
    if (!open || !menuRef.current || query.trim()) return
    const list = menuRef.current.querySelector<HTMLElement>('[data-chip-popover-list]')
    const active = menuRef.current.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!list || !active) return
    // Scroll so the current value is at the top — earlier/later times stay reachable.
    const align = () => {
      list.scrollTop = Math.max(0, active.offsetTop - 4)
    }
    align()
    const raf = window.requestAnimationFrame(align)
    return () => window.cancelAnimationFrame(raf)
  }, [open, value, filtered.length, query, menuStyle.maxHeight])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (rootRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
        btnRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!current && options.length === 0) return null

  const menu = open ? (
    <div
      ref={menuRef}
      id={listId}
      role="listbox"
      className={appearance==='field'?'kw-core-select-menu':undefined}
      aria-label={ariaLabel}
      onKeyDown={appearance === 'field' ? event => {
        const rows=Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]')??[])
        const index=rows.findIndex(row=>row===document.activeElement)
        let next:number|undefined
        if(event.key==='ArrowDown') next=Math.min(index+1,rows.length-1)
        if(event.key==='ArrowUp') next=Math.max(index-1,0)
        if(event.key==='Home') next=0
        if(event.key==='End') next=rows.length-1
        if(next!==undefined){event.preventDefault();event.stopPropagation();rows[next]?.focus();return}
        if(event.key==='Escape'){event.preventDefault();event.stopPropagation();setOpen(false);btnRef.current?.focus();return}
        if(event.key==='Tab'){setOpen(false);btnRef.current?.focus();return}
        if(event.key.length===1&&!event.ctrlKey&&!event.metaKey&&event.key!==' '&&event.target!==searchRef.current){
          const match=[...rows.slice(index+1),...rows.slice(0,index+1)].find(row=>row.textContent?.trim().toLocaleLowerCase().startsWith(event.key.toLocaleLowerCase()))
          if(match){event.preventDefault();match.focus()}
        }
      }:undefined}
      style={{
        ...menuStyle,
        background: color.darkMid,
        border: `1px solid ${color.darkBorder}`,
        borderRadius: radius.lg,
        boxShadow: shadow.popover,
      }}
    >
      {showSearch && (
        /* Solid chrome under Suche — sealed against sticky group headers (KA-1216) */
        <div
          style={{
            flexShrink: 0,
            position: 'relative',
            zIndex: 3,
            background: color.darkMid,
            padding: '8px 8px 6px',
          }}
        >
          <KwSearchField
            inputRef={searchRef}
            id={searchId}
            value={query}
            onChange={setQuery}
            placeholder={searchPlaceholder}
            aria-label={`${ariaLabel} suchen`}
            showIcon={false}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Escape') { e.preventDefault(); setOpen(false); btnRef.current?.focus(); return }
              if (e.key !== 'Enter') return
              e.preventDefault()
              const pick = customOpt ?? selectable.find((o) => !o.clear) ?? null
              if (!pick) return
              onChange(pick.value)
              setOpen(false)
              btnRef.current?.focus()
            }}
            style={{
              margin: 0,
              background: color.darkCard,
              borderRadius: radius.md,
              minHeight: 32,
              padding: '0 4px 0 10px',
            }}
            inputStyle={{ fontSize: 12, padding: '7px 0' }}
          />
        </div>
      )}
      <div
        data-chip-popover-list
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: showSearch ? '0 0 4px' : '4px 0',
          scrollbarWidth: 'thin',
          scrollbarColor: `${color.whiteAlpha(0.12)} transparent`,
        }}
      >
        {selectable.length === 0 ? (
          <p style={{ margin: '8px 12px', fontSize: 12, color: color.inkMuted, fontFamily: font.sans }}>
            Keine Treffer für „{query.trim()}“
          </p>
        ) : (
          displayRows.map((row) => {
            if (row.kind === 'header') {
              return (
                <div
                  key={`hdr-${row.key}`}
                  role="presentation"
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    padding: '8px 12px 6px',
                    fontSize: 10,
                    fontWeight: 650,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: color.inkSoft,
                    fontFamily: font.sans,
                    background: color.darkMid,
                    boxShadow: `0 1px 0 ${color.darkBorder}`,
                  }}
                >
                  {row.label}
                </div>
              )
            }
            const opt = row.opt
            const isActive = opt.value === value
            const title = opt.menuLabel ?? opt.label
            return (
              <React.Fragment key={opt.value || '__empty__'}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  tabIndex={appearance==='field'?-1:undefined}
                  onClick={() => {
                    onChange(opt.value)
                    setOpen(false)
                    btnRef.current?.focus()
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    textAlign: 'left',
                    background: isActive ? feed.cardActive : 'transparent',
                    border: 'none',
                    color: opt.clear
                      ? color.inkMuted
                      : isActive ? color.white : color.whiteAlpha(0.9),
                    padding: '8px 12px',
                    fontSize: opt.clear ? 11 : 12,
                    fontFamily: font.sans,
                    fontWeight: isActive ? 600 : opt.clear ? 500 : 400,
                    cursor: 'pointer',
                    minWidth: 0,
                    ...(appearance==='field'?{minHeight:'var(--kw-component-input-height)'}:{}),
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = feed.inputBg
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'
                  }}
                >
                  {opt.colorDot && (
                    <span
                      aria-hidden
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        flexShrink: 0,
                        background: opt.colorDot,
                      }}
                    />
                  )}
                  {opt.initials && (
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                      background: color.violetAlpha(0.25), color: color.violetDim,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 8, fontWeight: 700,
                    }}>
                      {opt.initials}
                    </span>
                  )}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: wrapOptionLabels ? undefined : 'ellipsis',
                      whiteSpace: wrapOptionLabels ? 'normal' : 'nowrap',
                      overflowWrap: wrapOptionLabels ? 'anywhere' : undefined,
                    }}>
                      {title}
                    </span>
                    {opt.subtitle && (
                      <span style={{
                        display: 'block',
                        fontSize: 10,
                        color: color.inkMuted,
                        marginTop: 1,
                        overflow: 'hidden',
                        textOverflow: wrapOptionLabels ? undefined : 'ellipsis',
                        whiteSpace: wrapOptionLabels ? 'normal' : 'nowrap',
                        overflowWrap: wrapOptionLabels ? 'anywhere' : undefined,
                      }}>
                        {opt.subtitle}
                      </span>
                    )}
                  </span>
                </button>
                {opt.dividerAfter && !query.trim() && (
                  <div
                    role="separator"
                    style={{ height: 1, margin: '4px 0', background: color.darkBorder }}
                  />
                )}
              </React.Fragment>
            )
          })
        )}
      </div>
    </div>
  ) : null

  const triggerPrefix = prefix ?? (
    current?.colorDot ? (
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: 2,
          flexShrink: 0,
          background: current.colorDot,
        }}
      />
    ) : null
  )

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex', maxWidth: '100%', width: fullWidth ? '100%' : undefined }}>
      <button
        {...triggerProps}
        ref={btnRef}
        className={appearance==='field'?'kw-property-chip kw-core-select-trigger':'kw-property-chip'}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open?listId:undefined}
        title={ariaLabel}
        aria-label={triggerProps?.['aria-label'] ?? (!prefix && ariaLabel ? `${ariaLabel}: ${current?.label ?? value}` : undefined)}
        aria-description={ariaLabel}
        onClick={() => { if (!disabled) setOpen((v) => !v) }}
        onKeyDown={appearance==='field'?event=>{if(['ArrowDown','ArrowUp'].includes(event.key)){event.preventDefault();setOpen(true)}}:undefined}
        style={{
          ...pillStyle(value, current?.label ?? ''),
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          paddingRight: 8,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          border: 'none',
          fontFamily: font.sans,
          maxWidth: '100%',
          ...(appearance === 'header' ? { ...headerControlStyle, padding: '0 var(--kw-semantic-space-control-gap) 0 var(--kw-semantic-space-control-inline)' } : {}),
          ...(appearance === 'field' ? { minHeight:'var(--kw-component-input-height)', padding:'0 var(--kw-component-input-padding-inline)', gap:'var(--kw-semantic-space-control-gap)', border:'1px solid var(--kw-component-input-border)', borderRadius:'var(--kw-component-input-radius)', background:'var(--kw-component-input-background)', color:'var(--kw-component-input-text)', font:'inherit', textAlign:'left', boxSizing:'border-box', minWidth:0 } as React.CSSProperties : {}),
          /* Kein minWidth:0 — sonst Ellipsis unter Intrinsic-Breite (z. B. „Kiwiloop“) */
          width: fullWidth ? '100%' : 'max-content',
        }}
      >
        {triggerPrefix}{triggerPrefix ? ' ' : null}
        <span style={{
          // The pill may use lineHeight: 1; ellipsis must not clip glyph descenders.
          lineHeight: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: '1 1 auto',
          minWidth: 0,
        }}>
          {current?.label}
        </span>
        {appearance === 'header'||appearance==='field' ? <ChevronDown size={14} aria-hidden style={{ marginLeft: 'auto', flexShrink: 0, color: color.inkMuted }} /> : <span style={{ opacity: 0.55, fontSize: 9, marginLeft: 2, flexShrink: 0 }} aria-hidden>▾</span>}
      </button>

      {typeof document !== 'undefined' && menu ? placement === 'auto' ? createPortal(<div role="region" aria-label={`${ariaLabel} Auswahl`} style={{ position: 'fixed', top: 0, left: 0, width: 0, height: 0, zIndex: menuZIndex }}>{menu}</div>, btnRef.current?.closest('dialog[open]') ?? document.body) : <AnchoredPopover anchor={btnRef} placement={placement}>{menu}</AnchoredPopover> : null}
    </div>
  )
}
